import React, { useEffect, useState } from "react";
import { Bot, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import AgentScopePicker from "../components/dialogs/settings/AgentScopePicker";
import { hostedAssistantNames } from "../lib/hostedAssistants";
import {
  finishHostedConnect,
  readHostedConnectRequest,
} from "../lib/hostedMcp";
import { fillTimezoneIfEmpty } from "../lib/syncBrowserTimezone";
import type { AgentConnection } from "../types";

function requestIdFromUrl(): string {
  return new URLSearchParams(window.location.search).get("rid") ?? "";
}

const ConnectAgentPage: React.FC = () => {
  const { user, isLoading, signInWithGoogle } = useAuth();
  const [rid] = useState(requestIdFromUrl);
  const [clientName, setClientName] = useState("Assistant");
  const [scope, setScope] = useState<AgentConnection["scope"]>("read_write");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) fillTimezoneIfEmpty(user.id);
  }, [user]);

  useEffect(() => {
    if (!user || !rid) return;
    let cancelled = false;
    readHostedConnectRequest(rid).then((result) => {
      if (cancelled) return;
      if (!result.ok) {
        setLoadError(
          typeof result.payload.error === "string"
            ? result.payload.error
            : "That sign-in link has expired. Start again from your assistant.",
        );
        return;
      }
      if (typeof result.payload.client_name === "string") {
        setClientName(result.payload.client_name);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [user, rid]);

  const go = async (
    choice: { scope: AgentConnection["scope"] } | { deny: true },
  ) => {
    setBusy(true);
    const result = await finishHostedConnect(rid, choice);
    if (result.ok && typeof result.payload.redirect === "string") {
      window.open(result.payload.redirect, "_self");
      return;
    }
    setLoadError(
      typeof result.payload.error === "string"
        ? result.payload.error
        : "Could not finish connecting. Please try again.",
    );
    setBusy(false);
  };

  // The three choices sit side by side, so the card widens while they show.
  const choosing = Boolean(rid && user && !loadError);

  return (
    <div className="min-h-screen bg-warm-bg-base flex items-center justify-center p-6">
      <div
        className={`bg-warm-bg-card rounded-card border border-warm-border shadow-soft-md p-8 w-full ${
          choosing ? "max-w-3xl" : "max-w-md"
        }`}
      >
        <div className="flex items-center gap-2 mb-4">
          <Bot size={18} className="text-warm-blue" />
          <h1 className="font-heading text-xl text-warm-text-primary">
            Connect JuDI
          </h1>
        </div>

        {!rid && (
          <p className="text-sm text-warm-text-secondary">
            This page is opened from {hostedAssistantNames("or")} when you add
            JuDI. Go back to that assistant, paste your JuDI address, and try
            again.
          </p>
        )}

        {rid && isLoading && (
          <div className="flex items-center gap-2 text-sm text-warm-text-tertiary">
            <Loader2 size={15} className="animate-spin" />
            Loading
          </div>
        )}

        {rid && !isLoading && !user && (
          <div className="space-y-4">
            <p className="text-sm text-warm-text-secondary">
              Sign in so {clientName} can talk about your tasks.
            </p>
            <button
              type="button"
              onClick={signInWithGoogle}
              className="text-sm text-white bg-warm-blue hover:bg-warm-blue-dark transition-colors px-3.5 py-2 rounded-lg"
            >
              Sign in
            </button>
          </div>
        )}

        {rid && user && loadError && (
          <p className="text-sm text-warm-text-secondary">{loadError}</p>
        )}

        {choosing && (
          <div className="space-y-4">
            <p className="text-sm text-warm-text-secondary">
              <strong className="text-warm-text-primary">{clientName}</strong>{" "}
              wants to connect to JuDI. Choose what it may do. You can turn
              this off later in Settings → Agents.
            </p>
            <AgentScopePicker
              name="connect-scope"
              value={scope}
              onChange={setScope}
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => go({ scope })}
                disabled={busy}
                className="text-sm text-white bg-warm-blue hover:bg-warm-blue-dark transition-colors px-3.5 py-2 rounded-lg disabled:opacity-50"
              >
                {busy ? "Connecting…" : "Connect JuDI"}
              </button>
              <button
                type="button"
                onClick={() => go({ deny: true })}
                disabled={busy}
                className="text-sm px-3.5 py-2 rounded-lg border border-warm-border text-warm-text-secondary hover:bg-warm-bg-elevated disabled:opacity-50"
              >
                Not now
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectAgentPage;
